/** A caixa de escrever: texto, @menção, comandos `/`, anexos, emojis e áudio. */
import { useEffect, useRef, useState } from "react";

import { lerComando, mencaoEmAndamento, novoIdDoCliente, sugerirComandos, sugerirPessoas } from "../../services/chat";
import { SeletorDeEmoji } from "./Emojis";
import { ICONE, Ic } from "./Icones";
import { OndaDaGravacao } from "./OndaDaGravacao";
import { tipoDoArquivo } from "./Midia";
import { PreviaDosAnexos } from "./PreviaDosAnexos";
import { TextoComEmojis } from "./TextoComEmojis";

/** Apertou e soltou antes disto = clique: a gravação fica ligada até o próximo clique. */
const LIMIAR_DO_CLIQUE_MS = 350;

/** `aoAgendar(texto)` (Fase C): abre a escolha da hora; devolve true quando agendou (a caixa limpa). */
export function Escrita({ aoEnviar, aoDigitar, respostaA, aoCancelarResposta, editando, aoCancelarEdicao, desabilitada, limiteMb, nomes = [], pode = () => true, aoEscolherTom, aoAgendar = null }) {
  const [texto, setTexto] = useState("");
  const [cursor, setCursor] = useState(0);
  const [escolhido, setEscolhido] = useState(0);
  const [arquivos, setArquivos] = useState([]);
  const [aviso, setAviso] = useState(null);
  // O microfone aberto agora: a onda lê o volume dele enquanto grava.
  const [fluxoGravando, setFluxoGravando] = useState(null);
  const [gravando, setGravando] = useState(false);
  // "segurando" (solte para enviar) ou "travada" (clique de novo para enviar).
  const [modoDaGravacao, setModoDaGravacao] = useState("segurando");
  const apertouEmRef = useRef(0);
  const soltouRef = useRef(false);
  const [emojisAbertos, setEmojisAbertos] = useState(false);
  const areaRef = useRef(null);
  const espelhoRef = useRef(null);
  const anexoRef = useRef(null);
  const cameraRef = useRef(null);
  const gravadorRef = useRef(null);
  const pedacosRef = useRef([]);
  const cancelouRef = useRef(false);
  const ultimoDigitarRef = useRef(0);
  // O id nasce com o RASCUNHO e só muda depois de enviado: tentar de novo (rede
  // lenta, Enter duas vezes) reenvia a MESMA mensagem, que o servidor não duplica.
  const idDoEnvioRef = useRef(novoIdDoCliente());
  const [enviando, setEnviando] = useState(false);
  const caixaRef = useRef({ texto: "", arquivos: [] });
  caixaRef.current = { texto, arquivos };

  // A conversa fechou com o microfone aberto: para e descarta.
  useEffect(() => () => {
    cancelouRef.current = true;
    const g = gravadorRef.current;
    if (g && g.state !== "inactive") g.stop();
    if (g && g.stream) g.stream.getTracks().forEach((t) => t.stop());
    gravadorRef.current = null;
  }, []);

  useEffect(() => { if (editando) { setTexto(editando.texto); areaRef.current?.focus(); } }, [editando]);
  useEffect(() => { if (respostaA) areaRef.current?.focus(); }, [respostaA]);

  const ajustarAltura = () => {
    const a = areaRef.current;
    if (!a) return;
    a.style.height = "auto";
    a.style.height = `${Math.min(a.scrollHeight, 160)}px`;
  };

  const adicionarArquivos = (lista) => {
    if (!lista) return;
    const novos = [];
    Array.from(lista).forEach((f) => {
      const limite = tipoDoArquivo(f) === "video" ? limiteMb * 4 : limiteMb;
      if (f.size > limite * 1024 * 1024) { setAviso(`${f.name} passa de ${limite} MB.`); return; }
      novos.push(f);
    });
    setArquivos((atuais) => [...atuais, ...novos]);
  };

  const sugestoes = editando ? [] : sugerirComandos(texto, pode);
  const mencao = editando ? null : mencaoEmAndamento(texto, cursor);
  const pessoas = mencao ? sugerirPessoas(mencao.termo, nomes) : [];
  const completarComando = (c) => {
    setTexto(c.pedeArgumento ? `/${c.nome} ` : `/${c.nome}`);
    setEscolhido(0);
    setTimeout(() => { areaRef.current?.focus(); if (!c.pedeArgumento) enviar(`/${c.nome}`); }, 0);
  };
  const completarMencao = (nome) => {
    if (!mencao) return;
    setTexto(`${texto.slice(0, mencao.inicio)}@${nome} ${texto.slice(cursor)}`);
    const pos = mencao.inicio + nome.length + 2;
    setTimeout(() => { const a = areaRef.current; if (!a) return; a.focus(); a.selectionStart = a.selectionEnd = pos; setCursor(pos); }, 0);
  };

  const enviar = async (conteudo) => {
    const limpo = (typeof conteudo === "string" ? conteudo : texto).trim();
    if ((!limpo && arquivos.length === 0) || enviando) return;
    const comando = editando ? null : lerComando(limpo);
    if (comando && comando.comando.pedeArgumento && !comando.argumento) { setAviso(`Falta o argumento: ${comando.comando.exemplo}`); return; }
    setEnviando(true);
    let foi = false;
    try {
      foi = await aoEnviar({
        texto: comando ? comando.legenda : limpo, arquivos, respostaA: respostaA?.id || null, idDoCliente: idDoEnvioRef.current,
        cartao: comando ? { tipo: comando.comando.tipo, ref: comando.argumento } : null,
      });
    } finally {
      setEnviando(false);
    }
    // Não foi: o texto e os arquivos FICAM na caixa (antes eram apagados junto com o erro).
    if (!foi) return;
    idDoEnvioRef.current = novoIdDoCliente();
    setTexto(""); setArquivos([]); setAviso(null);
    setTimeout(ajustarAltura, 0);
  };

  const pararGravacao = (cancelar) => {
    cancelouRef.current = cancelar;
    const g = gravadorRef.current;
    if (g && g.state !== "inactive") g.stop();
    gravadorRef.current = null;
  };

  const comecarGravacao = async () => {
    if (gravando) return;
    if (typeof MediaRecorder === "undefined") { setAviso("Este navegador não grava áudio."); return; }
    try {
      const fluxo = await navigator.mediaDevices.getUserMedia({ audio: true });
      const tipo = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const gravador = new MediaRecorder(fluxo, { mimeType: tipo });
      pedacosRef.current = []; cancelouRef.current = false;
      gravador.ondataavailable = (e) => e.data.size > 0 && pedacosRef.current.push(e.data);
      gravador.onstop = () => {
        fluxo.getTracks().forEach((t) => t.stop());
        setFluxoGravando(null);
        setGravando(false);
        if (cancelouRef.current || pedacosRef.current.length === 0) return;
        const blob = new Blob(pedacosRef.current, { type: tipo });
        const arquivo = new File([blob], `audio-${Date.now()}.${tipo === "audio/webm" ? "webm" : "m4a"}`, { type: tipo });
        // A foto (e o texto) que já estavam na caixa saem JUNTO com o áudio, numa mensagem só.
        const caixa = caixaRef.current;
        const junto = caixa.arquivos.length > 0 || caixa.texto.trim().length > 0;
        Promise.resolve(aoEnviar({
          texto: caixa.texto.trim(), arquivos: [...caixa.arquivos, arquivo], respostaA: respostaA?.id || null,
          idDoCliente: junto ? idDoEnvioRef.current : novoIdDoCliente(),
        })).then((foi) => {
          if (!foi || !junto) return;
          idDoEnvioRef.current = novoIdDoCliente();
          setTexto(""); setArquivos([]); setAviso(null);
          setTimeout(ajustarAltura, 0);
        });
      };
      gravadorRef.current = gravador;
      gravador.start();
      setFluxoGravando(fluxo);
      setGravando(true);
      // Segurou e já soltou enquanto o microfone abria (permissão): era um aperto longo, envia.
      if (soltouRef.current && Date.now() - apertouEmRef.current >= LIMIAR_DO_CLIQUE_MS) pararGravacao(false);
    } catch { setAviso("Sem acesso ao microfone. Libere no navegador para gravar."); }
  };

  /**
   * Como no WhatsApp: SEGURAR grava enquanto segura e soltar envia; um CLIQUE
   * liga a gravação, que fica até clicar de novo (no botão de enviar).
   */
  const apertarMicrofone = (e) => {
    e.preventDefault();
    apertouEmRef.current = Date.now();
    soltouRef.current = false;
    setModoDaGravacao("segurando");
    const aoSoltar = () => {
      window.removeEventListener("pointerup", aoSoltar);
      window.removeEventListener("pointercancel", aoSoltar);
      soltouRef.current = true;
      if (Date.now() - apertouEmRef.current < LIMIAR_DO_CLIQUE_MS) { setModoDaGravacao("travada"); return; }
      if (gravadorRef.current) pararGravacao(false);
    };
    window.addEventListener("pointerup", aoSoltar);
    window.addEventListener("pointercancel", aoSoltar);
    comecarGravacao();
  };

  const podeEnviar = !desabilitada && !enviando && (texto.trim().length > 0 || arquivos.length > 0);
  const inserirEmoji = (emoji) => {
    const a = areaRef.current;
    const inicio = a?.selectionStart ?? texto.length;
    const fim = a?.selectionEnd ?? texto.length;
    setTexto(`${texto.slice(0, inicio)}${emoji}${texto.slice(fim)}`);
    setTimeout(() => {
      if (!a) return;
      a.focus();
      a.selectionStart = a.selectionEnd = inicio + emoji.length;
      ajustarAltura();
    }, 0);
  };
  return (
    <div className="pchat-escrita">
      {editando && <div className="pchat-chip"><span>Editando a mensagem</span><button type="button" aria-label="Cancelar edição" onClick={() => { aoCancelarEdicao(); setTexto(""); }}><Ic d={ICONE.x} size={13} /></button></div>}
      {respostaA && !editando && (
        <div className="pchat-resposta" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}><b>Respondendo a {respostaA.autor_nome}</b><span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{respostaA.texto ? <TextoComEmojis texto={respostaA.texto} mencoes={false} /> : "anexo"}</span></div>
          <button type="button" className="pchat-btn" style={{ width: 28, height: 28 }} aria-label="Cancelar resposta" onClick={aoCancelarResposta}><Ic d={ICONE.x} size={13} /></button>
        </div>
      )}
      {arquivos.length > 0 && (
        <PreviaDosAnexos arquivos={arquivos} aoTirar={(i) => setArquivos((atuais) => atuais.filter((_, j) => j !== i))} />
      )}
      {(sugestoes.length > 0 || pessoas.length > 0) && !gravando && (
        <div className="pchat-menu-comandos" role="listbox">
          {pessoas.length > 0
            ? pessoas.map((n, i) => <button key={n} type="button" role="option" aria-selected={i === escolhido} className={i === escolhido ? "ativo" : ""} onMouseDown={(e) => e.preventDefault()} onClick={() => completarMencao(n)}><b>@{n}</b></button>)
            : sugestoes.map((c, i) => <button key={c.nome} type="button" role="option" aria-selected={i === escolhido} className={i === escolhido ? "ativo" : ""} onMouseDown={(e) => e.preventDefault()} onClick={() => completarComando(c)}><b>{c.exemplo}</b><span>{c.descricao}</span></button>)}
        </div>
      )}
      {gravando ? (
        <div className="pchat-gravacao">
          <button type="button" className="pchat-btn" aria-label="Cancelar gravação" onClick={() => pararGravacao(true)}><Ic d={ICONE.x} size={18} /></button>
          <span className="ponto" /><OndaDaGravacao fluxo={fluxoGravando} />
          <small style={{ color: "var(--ink-4)" }}>
            {modoDaGravacao === "travada" ? "gravando — toque no microfone para enviar" : "solte para enviar"}
            {arquivos.length > 0 ? ` · vai junto com ${arquivos.length === 1 ? "o anexo" : `os ${arquivos.length} anexos`}` : ""}
          </small>
          <button type="button" className="pchat-btn gravando" aria-label="Parar e enviar" onClick={() => pararGravacao(false)}><Ic d={ICONE.mic} size={20} /></button>
        </div>
      ) : (
        <>
          <div className="pchat-campo">
          <div className="espelho" aria-hidden="true" ref={espelhoRef}>{texto ? <TextoComEmojis texto={texto} mencoes={false} /> : null}{"\u200b"}</div>
          <textarea
            ref={areaRef}
            rows={1}
            onScroll={(e) => { if (espelhoRef.current) espelhoRef.current.scrollTop = e.target.scrollTop; }}
            placeholder={desabilitada ? "Esta conversa está só para leitura" : "Escreva uma mensagem"}
            value={texto}
            disabled={desabilitada}
            onSelect={(e) => setCursor(e.target.selectionStart || 0)}
            onChange={(e) => {
              setTexto(e.target.value); setCursor(e.target.selectionStart ?? e.target.value.length); setEscolhido(0); ajustarAltura();
              const agora = Date.now();
              if (agora - ultimoDigitarRef.current > 3000) { ultimoDigitarRef.current = agora; aoDigitar(); }
            }}
            onKeyDown={(e) => {
              const lista = pessoas.length > 0 ? pessoas.length : sugestoes.length;
              if (lista > 0 && (e.key === "ArrowDown" || e.key === "ArrowUp")) { e.preventDefault(); setEscolhido((i) => (i + (e.key === "ArrowDown" ? 1 : lista - 1)) % lista); return; }
              if (lista > 0 && (e.key === "Tab" || (e.key === "Enter" && !e.shiftKey && (pessoas.length > 0 || !texto.includes(" "))))) {
                e.preventDefault();
                if (pessoas.length > 0) completarMencao(pessoas[escolhido] || pessoas[0]); else completarComando(sugestoes[escolhido] || sugestoes[0]);
                return;
              }
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(); }
              else if (e.key === "Escape" && editando) { e.preventDefault(); aoCancelarEdicao(); setTexto(""); }
            }}
            onPaste={(e) => { if (e.clipboardData.files.length > 0) adicionarArquivos(e.clipboardData.files); }}
          />
          </div>
          <div className="ferramentas">
            <input ref={anexoRef} type="file" multiple hidden onChange={(e) => adicionarArquivos(e.target.files)} />
            {/* Fase B: no celular e no tablet abre direto a câmera (ADR-0077, decisão 11). */}
            <input ref={cameraRef} type="file" accept="image/*,video/*" capture="environment" hidden onChange={(e) => adicionarArquivos(e.target.files)} />
            <button type="button" className="pchat-btn" title="Anexar arquivo" aria-label="Anexar arquivo" disabled={desabilitada} onClick={() => anexoRef.current?.click()}><Ic d={ICONE.clip} size={18} /></button>
            <button type="button" className="pchat-btn" title="Câmera" aria-label="Tirar foto ou gravar vídeo" disabled={desabilitada || !!editando} onClick={() => cameraRef.current?.click()}><Ic d={ICONE.camera} size={18} /></button>
            <span style={{ position: "relative" }}>
              <button type="button" className={`pchat-btn ${emojisAbertos ? "gravando" : ""}`} title="Emoji" aria-label="Emoji" disabled={desabilitada} onClick={() => setEmojisAbertos((v) => !v)}><Ic d={ICONE.smile} size={18} /></button>
              {emojisAbertos && <SeletorDeEmoji aoFechar={() => setEmojisAbertos(false)} aoEscolher={inserirEmoji} aoEscolherTom={aoEscolherTom} />}
            </span>
            {aviso && <small style={{ color: "var(--warning-text)" }}>{aviso}</small>}
            <div className="direita">
              <button type="button" className="pchat-btn" title="Gravar áudio: clique para ligar e desligar, ou segure e solte" aria-label="Gravar áudio" disabled={desabilitada || !!editando} onPointerDown={apertarMicrofone}><Ic d={ICONE.mic} size={18} /></button>
              <button type="button" className="pchat-btn" title="Comandos: /pedido, /op, /entrega, /material, /sobra, /requisição, /lote…" aria-label="Comandos" disabled={desabilitada || !!editando} onClick={() => { setTexto("/"); setCursor(1); areaRef.current?.focus(); }}><span style={{ fontWeight: 800, fontFamily: "var(--font-mono)" }}>/</span></button>
              {aoAgendar && (
                <button
                  type="button" className="pchat-btn" title="Agendar esta mensagem" aria-label="Agendar mensagem"
                  disabled={desabilitada || !!editando || !texto.trim() || arquivos.length > 0}
                  onClick={async () => { if (await aoAgendar(texto.trim())) { setTexto(""); setTimeout(ajustarAltura, 0); } }}
                ><Ic d={ICONE.relogio} size={18} /></button>
              )}
              <button type="button" className="pchat-btn enviar" title={editando ? "Salvar" : "Enviar"} aria-label={editando ? "Salvar" : "Enviar"} disabled={!podeEnviar} onClick={enviar}><Ic d={editando ? ICONE.check : ICONE.send} size={18} /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
